"use client";
import ErrorComponent from "@/components/empty/ErrorComponent";
import Live from "@/components/scoreboard/Live";
import TvSvg from "@/components/scoreboard/TvSvg";
import {
  HEAD_TO_HEAD_PAGE_VIEWED,
  HEAD_TO_HEAD_STATUS_MAP,
} from "@/constants/amplitude";
import { EventStatus, GetHeadToHeadHeaderQuery } from "@/graphql/gql/graphql";
import {
  getFormattedDayMonthYear,
  getFormattedMatchupHeaderDate,
} from "@/utils/date";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import useOnce from "@/utils/hooks/useOnce";
import TimeStampClient from "@/utils/TimeStampClient";
import GameInfo from "./GameInfo";
import MatchupMainWrapper from "./TeamMatchups/MatchupMainWrapper";
import TeamMatchups from "./TeamMatchups/TeamMatchups";
import styles from "./Header.module.scss";

interface HeaderProps {
  headerData: GetHeadToHeadHeaderQuery["event"];
}

export default function HeaderContainer({ headerData }: HeaderProps) {
  const { eventName, gameState, homeTeam, awayTeam, broadcastInfo, startTime } =
    useHeadToHeadContext();
  const { logAmplitudeEvent, setPage } = useAmplitude();
  useOnce(() => {
    setPage("headToHead");
    logAmplitudeEvent(HEAD_TO_HEAD_PAGE_VIEWED, {
      "Event Name": eventName,
      "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
    });
  });
  if (!homeTeam || !awayTeam) {
    return (
      <ErrorComponent
        headline="No event data available"
        subtext="Event data is missing. Please try again later."
      />
    );
  }
  const tvNetworkName = broadcastInfo?.network ?? "";
  const isLive = gameState === EventStatus.Live;
  const isFinal = gameState === EventStatus.Final;

  return (
    <div className={styles.headerContainer}>
      <div className={styles.header}>
        <div className={styles.topInfo}>
          <div className={styles.labelContainer}>
            <div className={styles.tvContainer}>
              {!isFinal && <TvSvg />}
              {!isFinal && <span>{tvNetworkName}</span>}
              {isFinal && <span className={styles.final}>Final</span>}
            </div>
            {!isLive && !isFinal && (
              <span>
                <TimeStampClient
                  isoString={startTime}
                  formatter={getFormattedMatchupHeaderDate}
                />
              </span>
            )}
            {isLive && (
              <span>
                <Live gameTime="" />
              </span>
            )}
            {isFinal && (
              <span>
                <TimeStampClient
                  isoString={startTime}
                  formatter={getFormattedDayMonthYear}
                />
              </span>
            )}
          </div>
        </div>
        <MatchupMainWrapper />

        <TeamMatchups headerData={headerData} />

        <GameInfo weather={headerData?.weather} isMobile={true} />
      </div>
    </div>
  );
}
